// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import * as Redux from 'redux';
import OlMap from 'ol/map';
import TileWMS from 'ol/source/tilewms';
import Tile from 'ol/layer/tile';
import OlFormatGeoJSON from 'ol/format/geojson';
import OlSourceVector from 'ol/source/vector';
import OlLayerVector from 'ol/layer/vector';
import OlStyleStyle from 'ol/style/style';
import OlStyleIcon from 'ol/style/icon';
import OlStyleStroke from 'ol/style/stroke';
import OlStyleFill from 'ol/style/fill';
import OlStyleCircle from 'ol/style/circle';
import OlText from 'ol/style/text';
import OlLoadingstrategy from 'ol/loadingstrategy';
import { setOlLayers } from './../../../../actions/map';

type StyleConfig = {
  id: number,
  name: String,
  radius: number,
  fillColor: String,
  strokeWidth: number,
  strokeColor: String,
  opacity: number,
  legendIcon: String,
  symbolIcon: String
}

type LayerConfig = {
  id: number,
  name: String,
  defaultVisibility: boolean,
  order: number,
  url: String,
  type: number,
  style: StyleConfig
}

type Props = {
  olMap: OlMap,
  layersConfig: Array<LayerConfig>
}

export class GeoserverLayers extends React.Component<Props> {
  geoserverLayersInit: boolean = false;

  initLayersFromConfig(): void {
    const { layersConfig }: Props = this.props;
    let i = layersConfig.length - 1;
    layersConfig.forEach(layer => {
      layer.order = i;
      i--;
      switch (layer.type) {
        case 4:
        case 5:
          this.initGeoserverWMSLayer(layer);
          break;
        default:
          this.initGeoserverWFSLayer(layer);
          break;
      }
    });
  }

  initGeoserverWFSLayer(layer: LayerConfig): void {
    const vectorSource = new OlSourceVector({
      format: new OlFormatGeoJSON(),
      crossOrigin: 'anonymous',
      url: function (extent) { return layer.url + '&bbox=' + extent.join(',') + ',EPSG:3857'; },
      strategy: OlLoadingstrategy.bbox
    });

    const vectorLayer = new OlLayerVector({
      id: layer.id,
      visible: layer.defaultVisibility,
      name: layer.name,
      groupName: layer.groupName,
      zIndex: layer.order,
      private: layer.private,
      showInLegend: true,
      opacity: layer.style.opacity,
      legendIcon: layer.style.legendIcon,
      source: vectorSource,
      styleId: layer.style.id,
      color: layer.style.fillColor || layer.style.strokeColor,
      type: layer.type,
      describeUrl: layer.url.replace('GetFeature','DescribeFeatureType'),
      style: (feature, resolution) => {
        return this.createVectorStyle(layer, feature, resolution)
      }
    });
    this.props.olMap.addLayer(vectorLayer);
  }

  initGeoserverWMSLayer(layer: LayerConfig): void {
    const rasterSource = new TileWMS(({
      url: layer.url,
      serverType: 'geoserver'
    }));

    const rasterLayer = new Tile({
      id: layer.id,
      visible: layer.defaultVisibility,
      name: layer.name,
      groupName: layer.groupName,
      zIndex: layer.order,
      private: layer.private,
      showInLegend: true,
      opacity: layer.style.opacity,
      legendIcon: layer.style.legendIcon,
      legendGraphic: layer.legendUrl,
      source: rasterSource,
      styleId: layer.style.id,
      type: layer.type,
    });
    this.props.olMap.addLayer(rasterLayer);
  }

  // TODO: remove
  initResourceGeoJsonLayer(layer: LayerConfig): void {
    // TODO: OlLoadingstrategy.bbox support 
    const token = localStorage.getItem('token');
    const { BASE_URL } = this.props;

    const vectorSource = new OlSourceVector({
      format: new OlFormatGeoJSON(),
      crossOrigin: 'anonymous',
      url: function () { return BASE_URL + layer.url + '?token=' + token },
      strategy: OlLoadingstrategy.all
    });

    const vectorLayer = new OlLayerVector({
      visible: layer.defaultVisibility,
      resourceId: layer.resourceId,
      name: layer.name,
      groupName: layer.groupName,
      zIndex: layer.order,
      private: layer.private,
      showInLegend: true,
      opacity: layer.style.opacity,
      legendIcon: layer.style.legendIcon,
      source: vectorSource,
      styleId: layer.style.id,
      type: layer.type,
      color: layer.style.fillColor || layer.style.strokeColor,
      style: (feature, resolution) => {
        return this.createVectorStyle(layer, feature, resolution)
      }
    });
    this.props.olMap.addLayer(vectorLayer);
  }

  createVectorStyle(layer, feature, resolution): OlStyleStyle {
    const { labelsConfig } = this.props;
    const { type, style } = layer;
    const { symbolIcon } = style;

    const currentLayerLabelConfig = labelsConfig.find(config => config.layerName === layer.name);
    switch (type) {
      case 1:
        if (symbolIcon) {
          return new OlStyleStyle({
            image: new OlStyleIcon({
              src: symbolIcon
            })
          });
        } else {
          return new OlStyleStyle({
            //TODO: dodać w pozostałych przypadkach
            text: currentLayerLabelConfig
              ? this.createTextStyle(feature, resolution, currentLayerLabelConfig.values)
              : null,
            image: new OlStyleCircle({
              radius: style.radius,
              fill: new OlStyleFill({
                color: style.fillColor
              })
            })
          });
        }
      case 2:
        return new OlStyleStyle({
          stroke: new OlStyleStroke({
            color: style.strokeColor,
            width: style.strokeWidth
          })
        });
      case 3:
        return new OlStyleStyle({
          stroke: new OlStyleStroke({
            color: style.strokeColor,
            width: style.strokeWidth
          }),
          fill: new OlStyleFill({
            color: style.fillColor
          })
        });
      default:
        return new OlStyleStyle({
          image: new OlStyleCircle({
            radius: style.radius,
            fill: new OlStyleFill({
              color: style.fillColor
            })
          })
        });
    }
  }

  removeConfigLayers(): void {
    const { olMap }: Props = this.props
    olMap.getLayers().array_
      .filter(layer => layer.values_.type)
      .forEach(layer => olMap.removeLayer(layer));
  }

  createTextStyle = (feature, resolution, config) => {
    const align = config.align;
    const baseline = config.baseline;
    const size = config.size;
    const offsetX = parseInt(config.offsetX, 10);
    const offsetY = parseInt(config.offsetY, 10);
    const weight = config.weight;
    const placement = config.placement ? config.placement : undefined;
    const maxAngle = config.maxangle ? parseFloat(config.maxangle) : undefined;
    const overflow = config.overflow ? (config.overflow === 'true') : undefined;
    const rotation = parseFloat(config.rotation);
    const font = weight + ' ' + size + ' ' + config.font;
    const fillColor = config.color;
    const outlineColor = config.outline;
    const outlineWidth = parseInt(config.outlineWidth, 10);

    const text = new OlText({
      textAlign: align === '' ? undefined : align,
      textBaseline: baseline,
      font: font,
      text: this.getText(feature, resolution, config),
      fill: new OlStyleFill({ color: fillColor }),
      stroke: new OlStyleStroke({ color: outlineColor, width: outlineWidth }),
      offsetX: offsetX,
      offsetY: offsetY,
      placement: placement,
      maxAngle: maxAngle,
      overflow: overflow,
      rotation: rotation
    });
    return text;
  };

  getText = (feature, resolution, config) => {
    const maxResolution = config.maxreso;
    let text = String(feature.get(config.attribute));
    if (resolution > maxResolution)
      text = '';
    return text;
  };

  componentDidUpdate(): void {
    if (this.geoserverLayersInit === false && this.props.layersConfig) {
      this.geoserverLayersInit = true;
      this.initLayersFromConfig();
      this.props.setOlLayers(this.props.olMap.getLayers());
    } else {
      this.props.setOlLayers(null);
      this.removeConfigLayers();
      this.initLayersFromConfig();
      this.props.setOlLayers(this.props.olMap.getLayers());
    }
  }

  render() {
    return (null);
  }
};

const mapStateToProps = state => ({
  labelsConfig: state.mapData.get('labelsConfig'),
  layersConfig: state.mapData.get('layersConfig'),
  axiosInstance: state.auth.get('axiosInstance'),
  BASE_URL: state.domainProperties.get('BASE_URL'),
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
  setOlLayers,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(GeoserverLayers);


// WEBPACK FOOTER //
// ./src/containers/content/map/geoserverLayers/GeoserverLayers.jsx